import React, { useState } from 'react';
import axios from 'axios';
import MfaSetupForm from './MfaSetupForm';
import LoginCredentialsForm from './LoginCredentialsForm';
import SignupForm from './SignupForm';
import LoginOtpForm from './LoginOtpForm';

const BASE_URL = 'https://api.proinsight.com/mfa';


const SuccessMessage = ({ message }) => message ? (
    <div style={{
        background: '#e6ffed',
        color: '#006400',
        padding: '16px',
        borderRadius: '8px',
        marginBottom: '24px',
        textAlign: 'center',
    }}>
        {message}
    </div>
) : null;

const ErrorMessage = ({ message }) => message ? (
    <div style={{
        background: '#ffebee',
        color: '#c62828',
        padding: '16px',
        borderRadius: '8px',
        marginBottom: '24px',
        textAlign: 'center',
    }}>
        {message}
    </div>
) : null;



function AuthPage() {
    const [view, setView] = useState('login');

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const clearMessages = () => { setError(''); setSuccess(''); };

    // Login states
    const [loginUsername, setLoginUsername] = useState('');
    const [loginPassword, setLoginPassword] = useState('');
    const [otp, setOtp] = useState('');
    const [loginStep, setLoginStep] = useState('credentials');

    // Signup states
    const [signupUsername, setSignupUsername] = useState('');
    const [signupEmail, setSignupEmail] = useState('');
    const [signupPassword, setSignupPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState("");
    // MFA setup states
    const [qrCode, setQrCode] = useState(null);
    const [mfaSecret, setMfaSecret] = useState('');

    // ── Handlers ───────────────────────────────────────────────────────

    const handleSignup = async (e) => {
        e.preventDefault();
        clearMessages();
        setLoading(true);

        try {
            const res = await axios.post(`${BASE_URL}/create_user`, {
                username: signupUsername,
                password: signupPassword,
                email: signupEmail,
            });
            setSuccess(res.data.message || 'Account created successfully!');
            setView('mfa-prompt');
        } catch (err) {
            setError(err.response?.data?.error || 'Sign up failed');
        } finally {
            setLoading(false);
        }
    };

    const startMfaSetup = async () => {
        clearMessages();
        setLoading(true);
        const usernameToUse = signupUsername || loginUsername;

        try {
            const res = await axios.post(`${BASE_URL}/setup`, {
                username: usernameToUse,
                password: signupPassword || loginPassword, // fallback – in real app use session/challenge token
            });

            setQrCode(res.data.qr_code);
            setMfaSecret(res.data.secret);
            setView('mfa-setup');
            setSuccess('Scan the QR code below with your authenticator app');
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to generate MFA setup');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyMfaSetup = async (e) => {
        e.preventDefault();
        if (otp.length !== 6) {
            setError('Please enter a valid 6-digit code');
            return;
        }

        clearMessages();
        setLoading(true);

        try {
            await axios.post(`${BASE_URL}/verify_and_enable`, {
                username: signupUsername || loginUsername,
                otp,
            });

            setSuccess('MFA has been enabled successfully!');
            setTimeout(() => {
                window.location.href = '/homeowner-concierge';
            }, 1800);
        } catch (err) {
            setError(err.response?.data?.error || 'Invalid or expired code');
        } finally {
            setLoading(false);
        }
    };

    const skipMfa = () => {
        window.location.href = '/homeowner-concierge';
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        clearMessages();
        setLoading(true);

        try {
            const res = await axios.post(`${BASE_URL}/login`, {
                username: loginUsername,
                password: loginPassword,
            });

            if (res.data.mfa_required) {
                setLoginStep('otp');
                setSuccess('MFA verification required');
            } else if (!res.data.mfa_required) {
                // localStorage.setItem('accessToken', res.data.access)
                localStorage.setItem('authTokens', JSON.stringify(res.data))
                setSuccess('Login successful!');
                // window.location.href = '/homeowner-concierge';
                setView('mfa-prompt');
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Login failed');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        if (otp.length !== 6) {
            setError('Please enter a valid 6-digit code');
            return;
        }

        clearMessages();
        setLoading(true);

        try {
            const res = await axios.post(`${BASE_URL}/verify_login`, {
                username: loginUsername,
                otp,
            });

            setSuccess('Login successful!');
            let accessToken = res.data.access;
            // localStorage.setItem('accessToken', accessToken);
            localStorage.setItem('authTokens', JSON.stringify(res.data))
            setTimeout(() => {
                window.location.href = '/homeowner-concierge';
            }, 500);
        } catch (err) {
            setError(err.response?.data?.error || 'Invalid OTP');
        } finally {
            setLoading(false);
        }
    };

    // ── Render ─────────────────────────────────────────────────────────

    const currentUsername = signupUsername || loginUsername;

    return (
        <>
            <div className="left-container">
                <img
                    src="https://storage.googleapis.com/msgsndr/sIF5ZS1VxhUZsYbvq7gL/media/6655d126ef816ee29da4a24c.jpeg"
                    alt="branding / welcome image"
                />
            </div>

            <div className="right-container authPageContainer">
                <div className="slide slide0">
                    <div style={{
                        // maxWidth: '420px',
                        margin: '48px auto',
                        padding: '32px',
                        // borderRadius: '12px',
                        // boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    }}>
                        <h3 style={{ textAlign: 'center', marginBottom: '24px' }}>
                            {view === 'login' ? 'Login' :
                                view === 'signup' ? 'Create Account' :
                                    view === 'mfa-prompt' ? 'Secure Your Account' :
                                        'Set Up Two-Factor Authentication'}
                        </h3>

                        <SuccessMessage message={success} />
                        <ErrorMessage message={error} />

                        {view === 'login' && loginStep === 'credentials' && (
                            <LoginCredentialsForm
                                username={loginUsername}
                                setUsername={setLoginUsername}
                                password={loginPassword}
                                setPassword={setLoginPassword}
                                loading={loading}
                                onSubmit={handleLogin}
                            />
                        )}

                        {view === 'login' && loginStep === 'otp' && (
                            <LoginOtpForm
                                otp={otp}
                                setOtp={setOtp}
                                loading={loading}
                                onSubmit={handleVerifyOtp}
                                onBack={() => { setLoginStep('credentials'); setOtp(''); }}
                            />
                        )}

                        {/* {view === 'signup' && (
                            <SignupForm
                                username={signupUsername}
                                setUsername={setSignupUsername}
                                email={signupEmail}
                                setEmail={setSignupEmail}
                                password={signupPassword}
                                setPassword={setSignupPassword}
                                confirmPassword={confirmPassword}
                                setConfirmPassword={setConfirmPassword}
                                loading={loading}
                                onSubmit={handleSignup}
                            />
                        )} */}

                        {view === 'mfa-prompt' && (
                            <div style={{ textAlign: 'center' }}>
                                <p style={{ fontSize: '16px', marginBottom: '24px', lineHeight: '1.5' }}>
                                    For better security, enable Multi-Factor Authentication (MFA).<br />
                                    It takes less than 1 minute and protects your account.
                                </p>

                                <button
                                    onClick={startMfaSetup}
                                    style={{
                                        width: '100%',
                                        padding: '14px',
                                        background: '#28a745',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontSize: '17px',
                                        marginBottom: '16px',
                                        cursor: 'pointer',
                                    }}
                                >
                                    Set up MFA now (recommended)
                                </button>

                                <button
                                    onClick={skipMfa}
                                    style={{
                                        width: '100%',
                                        padding: '14px',
                                        background: 'transparent',
                                        border: '1px solid #666',
                                        color: '#666',
                                        borderRadius: '6px',
                                        fontSize: '17px',
                                        cursor: 'pointer',
                                    }}
                                >
                                    Skip for now
                                </button>
                            </div>
                        )}

                        {view === 'mfa-setup' && (
                            <MfaSetupForm
                                qrCode={qrCode}
                                secret={mfaSecret}
                                otp={otp}
                                setOtp={setOtp}
                                loading={loading}
                                onSubmit={handleVerifyMfaSetup}
                                onSkip={skipMfa}
                            />
                        )}

                        {/* {(view === 'login' || view === 'signup') && (
                            <div style={{ textAlign: 'center', marginTop: '24px', fontSize: '14px', color: '#666' }}>
                                {view === 'login' ? (
                                    <button
                                        onClick={() => setView('signup')}
                                        style={{ background: 'none', border: 'none', color: '#cfd1d3', cursor: 'pointer', textDecoration: 'underline' }}
                                    >
                                        Don't have an account? Sign up
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => setView('login')}
                                        style={{ background: 'none', border: 'none', color: '#cfd1d3', cursor: 'pointer', textDecoration: 'underline' }}
                                    >
                                        Already have an account? Login
                                    </button>
                                )}
                            </div>
                        )} */}
                    </div>
                </div>
            </div>
        </>
    );
}

export default AuthPage;